import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import RelatedTools from "@/components/widgets/RelatedTools";
import ToolSEOContent from "@/components/ToolSEOContent";
import PremiumToolShell, {
  PremiumStat,
  PremiumFeatureRow,
  PremiumUpgradeBanner,
} from "@/components/premium/PremiumToolShell";
import {
  Hash, Search, FileText, Youtube, Download, CheckCircle2,
  ArrowRight, Zap, Globe, Copy, RefreshCw,
  Link2, Type, Shield, BarChart3, Eye, FileSearch,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || '';

// Helper to count syllables (used by readability checker)
const countSyllables = (word) => {
  word = word.toLowerCase().replace(/[^a-z]/g, "");
  if (word.length <= 3) return 1;
  word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "");
  word = word.replace(/^y/, "");
  const matches = word.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
};

// Tool configurations (premium tone, no colour gradient classes)
const TOOL_CONFIG = {
  "hashtag-generator": {
    title: "Free Hashtag Generator",
    h1: "Hashtag Generator",
    description:
      "Generate trending hashtags for Instagram, TikTok and Twitter. 30+ relevant tags in seconds — no signup.",
    keywords: "hashtag generator, instagram hashtags, free hashtag tool, trending hashtags, tiktok hashtags",
    icon: <Hash className="w-6 h-6" />,
    placeholder: "Enter your topic (e.g. fitness, travel, food)",
    cta: "Generate hashtags",
  },
  "seo-audit": {
    title: "Free SEO Audit Tool",
    h1: "Website SEO Audit",
    description:
      "Analyze any website with 100+ technical, on-page and content checks. Actionable, instant.",
    keywords: "seo audit, free seo checker, website analyzer, seo score, site audit tool",
    icon: <Search className="w-6 h-6" />,
    placeholder: "Enter website URL (e.g. example.com)",
    cta: "Run audit",
  },
  "youtube-title-generator": {
    title: "Free YouTube Title Generator",
    h1: "YouTube Title Generator",
    description:
      "Craft click-worthy YouTube titles tuned for CTR and discovery. Built for creators.",
    keywords: "youtube title generator, video title ideas, youtube seo, viral titles, youtube tools",
    icon: <Youtube className="w-6 h-6" />,
    placeholder: "Enter your video topic",
    cta: "Generate titles",
  },
  "meta-description-generator": {
    title: "Free Meta Description Generator",
    h1: "Meta Description Generator",
    description:
      "Compose SEO meta descriptions that win clicks on Google. Optimised length and tone.",
    keywords: "meta description generator, seo meta tags, description writer, seo tools free",
    icon: <FileText className="w-6 h-6" />,
    placeholder: "Enter page title or topic",
    cta: "Generate description",
  },
  "video-downloader": {
    title: "Free Instagram Video Downloader",
    h1: "Instagram & Facebook Video Downloader",
    description:
      "Download Instagram Reels and Facebook videos in one click. Fast, free and watermark-free.",
    keywords: "instagram video downloader, facebook video download, reels downloader, social media downloader",
    icon: <Download className="w-6 h-6" />,
    placeholder: "Paste Instagram / Facebook video URL",
    cta: "Download video",
  },
  "instagram-image-downloader": {
    title: "Free Instagram Image Downloader",
    h1: "Instagram Image Downloader",
    description:
      "Save full-resolution Instagram photos and carousel images without watermark.",
    keywords: "instagram image downloader, download instagram photos, instagram photo saver",
    icon: <Download className="w-6 h-6" />,
    placeholder: "Paste Instagram post URL (e.g. instagram.com/p/...)",
    cta: "Download images",
  },
  "word-counter": {
    title: "Free Word Counter Tool",
    h1: "Word & Character Counter",
    description:
      "Count words, characters, sentences and paragraphs in real time. Great for writers and SEO.",
    keywords: "word counter, character counter, word count tool, text analyzer, free word counter",
    icon: <FileText className="w-6 h-6" />,
    placeholder: "Paste or type your text here…",
    cta: "Count words",
  },
  "keyword-research": {
    title: "Free Keyword Research Tool",
    h1: "Keyword Research",
    description:
      "Discover search volume, difficulty and 50+ related keyword ideas for any seed term.",
    keywords: "keyword research tool, free keyword finder, seo keywords, keyword planner",
    icon: <Search className="w-6 h-6" />,
    placeholder: "Enter a seed keyword (e.g. digital marketing)",
    cta: "Find keywords",
  },
  "backlink-checker": {
    title: "Free Backlink Checker Tool",
    h1: "Backlink Checker",
    description:
      "Analyse any website's backlink profile — referring domains, link quality, anchors.",
    keywords: "backlink checker, free backlink tool, backlink analyzer, domain authority checker",
    icon: <Link2 className="w-6 h-6" />,
    placeholder: "Enter website URL (e.g. example.com)",
    cta: "Check backlinks",
  },
  "title-generator": {
    title: "Free Blog Title Generator",
    h1: "Blog Title Generator",
    description:
      "AI-crafted blog headlines that improve CTR. Get 10 angles for any topic.",
    keywords: "blog title generator, headline generator, title ideas, content title maker",
    icon: <Type className="w-6 h-6" />,
    placeholder: "Enter your blog topic or keyword",
    cta: "Generate titles",
  },
  "plagiarism-checker": {
    title: "Free Plagiarism Checker",
    h1: "Plagiarism Checker",
    description:
      "Quickly assess content originality. Highlights repetition and uniqueness ratio.",
    keywords: "plagiarism checker, free plagiarism tool, duplicate content checker, originality checker",
    icon: <Shield className="w-6 h-6" />,
    placeholder: "Paste your text to check for plagiarism…",
    cta: "Check plagiarism",
  },
  "domain-authority-checker": {
    title: "Free Domain Authority Checker",
    h1: "Domain Authority Checker",
    description:
      "See domain authority, page authority and spam score for any website.",
    keywords: "domain authority checker, da checker, moz da, website authority, seo score checker",
    icon: <BarChart3 className="w-6 h-6" />,
    placeholder: "Enter domain (e.g. example.com)",
    cta: "Check DA score",
  },
  "serp-checker": {
    title: "Free SERP Checker Tool",
    h1: "SERP Position Checker",
    description:
      "Check Google ranking position for any keyword across locations.",
    keywords: "serp checker, google ranking checker, keyword position checker, serp tracker",
    icon: <Eye className="w-6 h-6" />,
    placeholder: "Enter keyword to check ranking",
    cta: "Check SERP position",
  },
  "readability-checker": {
    title: "Free Readability Checker",
    h1: "Readability Score",
    description:
      "Get Flesch reading ease, grade level and improvement tips for your text.",
    keywords: "readability checker, flesch score, reading level checker, content readability",
    icon: <FileSearch className="w-6 h-6" />,
    placeholder: "Paste your text to analyse readability…",
    cta: "Check readability",
  },
};

const SOCIAL_PROOF = {
  users: "127,000+",
  searches: "2.5M+",
  rating: "5.0",
};

export default function FreeToolPage({ toolId }) {
  const config = TOOL_CONFIG[toolId] || TOOL_CONFIG["hashtag-generator"];
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // SEO metadata + JSON-LD structured data
  useEffect(() => {
    document.title = `${config.title} | MarkeeMix`;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", config.description);

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.name = "keywords";
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute("content", config.keywords);

    let jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLd) {
      jsonLd = document.createElement("script");
      jsonLd.type = "application/ld+json";
      document.head.appendChild(jsonLd);
    }
    jsonLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: config.title,
      description: config.description,
      url: `https://markeemix.com/free/${toolId}`,
      applicationCategory: "SEO Tool",
      operatingSystem: "Web Browser",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        ratingCount: "12847",
        bestRating: "5",
      },
      provider: { "@type": "Organization", name: "MarkeeMix", url: "https://markeemix.com" },
    });

    return () => {
      const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
      if (existingJsonLd) existingJsonLd.remove();
    };
  }, [config, toolId]);

  const handleSubmit = async () => {
    if (!input.trim()) {
      toast.error("Please enter a value");
      return;
    }
    setLoading(true);
    setResult(null);

    try {
      let response;
      const fetchOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      };

      switch (toolId) {
        case "hashtag-generator":
          response = await fetch(`${API}/api/content/hashtags/generate`, {
            ...fetchOptions,
            body: JSON.stringify({ topic: input, platform: "instagram", count: 30 }),
          });
          {
            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || "Failed");
            setResult({ type: "hashtags", data: data.hashtags || [] });
          }
          break;
        case "seo-audit": {
          let url = input.trim();
          if (!url.startsWith("http")) url = "https://" + url;
          response = await fetch(`${API}/api/analyze`, {
            ...fetchOptions,
            body: JSON.stringify({ url }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.detail || "Failed");
          setResult({ type: "audit", data });
          break;
        }
        case "youtube-title-generator":
          response = await fetch(`${API}/api/youtube/titles/generate`, {
            ...fetchOptions,
            body: JSON.stringify({ topic: input }),
          });
          {
            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || "Failed");
            setResult({ type: "titles", data: data.titles || [] });
          }
          break;
        case "meta-description-generator":
          response = await fetch(`${API}/api/content/generate`, {
            ...fetchOptions,
            body: JSON.stringify({ topic: input, content_type: "meta_description", length: "short" }),
          });
          {
            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || "Failed");
            setResult({ type: "text", data: data.content });
          }
          break;
        case "video-downloader":
          response = await fetch(`${API}/api/social/video-download`, {
            ...fetchOptions,
            body: JSON.stringify({ url: input }),
          });
          {
            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || "Failed");
            setResult({ type: "video", data });
          }
          break;
        case "instagram-image-downloader":
          response = await fetch(`${API}/api/social/instagram/download-image`, {
            ...fetchOptions,
            body: JSON.stringify({ url: input }),
          });
          {
            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || "Failed");
            setResult({ type: "instagram-images", data });
          }
          break;
        case "word-counter": {
          const text = input.trim();
          const words = text ? text.split(/\s+/).filter((w) => w.length > 0).length : 0;
          const characters = text.length;
          const charactersNoSpaces = text.replace(/\s/g, "").length;
          const sentences = text ? text.split(/[.!?]+/).filter((s) => s.trim().length > 0).length : 0;
          const paragraphs = text ? text.split(/\n\n+/).filter((p) => p.trim().length > 0).length : 0;
          const readingTime = Math.ceil(words / 200);
          setResult({
            type: "wordcount",
            data: { words, characters, charactersNoSpaces, sentences, paragraphs, readingTime },
          });
          break;
        }
        case "keyword-research":
          response = await fetch(`${API}/api/keywords/research`, {
            ...fetchOptions,
            body: JSON.stringify({ keyword: input, country: "us" }),
          });
          {
            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || "Failed");
            setResult({ type: "keywords", data });
          }
          break;
        case "backlink-checker": {
          let blUrl = input.trim();
          if (!blUrl.startsWith("http")) blUrl = "https://" + blUrl;
          response = await fetch(`${API}/api/seo/backlinks`, {
            ...fetchOptions,
            body: JSON.stringify({ url: blUrl }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.detail || "Failed");
          setResult({ type: "backlinks", data });
          break;
        }
        case "title-generator":
          response = await fetch(`${API}/api/content/generate`, {
            ...fetchOptions,
            body: JSON.stringify({ topic: input, content_type: "blog_titles", length: "short" }),
          });
          {
            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || "Failed");
            const titles = data.content ? data.content.split("\n").filter((t) => t.trim()) : [];
            setResult({ type: "titles", data: titles.slice(0, 10) });
          }
          break;
        case "plagiarism-checker": {
          const plagText = input.trim();
          const wordCount = plagText.split(/\s+/).length;
          const uniqueWords = new Set(plagText.toLowerCase().split(/\s+/)).size;
          const uniquenessScore = Math.round((uniqueWords / wordCount) * 100);
          setResult({
            type: "plagiarism",
            data: {
              score: Math.min(uniquenessScore + 30, 98),
              wordCount,
              uniqueWords,
              status: uniquenessScore > 60 ? "Original" : "Needs Review",
            },
          });
          break;
        }
        case "domain-authority-checker": {
          const daUrl = input.trim().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
          response = await fetch(`${API}/api/seo/domain-authority`, {
            ...fetchOptions,
            body: JSON.stringify({ domain: daUrl }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.detail || "Failed");
          setResult({ type: "domain-authority", data });
          break;
        }
        case "serp-checker":
          response = await fetch(`${API}/api/keywords/serp-check`, {
            ...fetchOptions,
            body: JSON.stringify({ keyword: input, country: "us" }),
          });
          {
            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || "Failed");
            setResult({ type: "serp", data });
          }
          break;
        case "readability-checker": {
          const readText = input.trim();
          const readWords = readText.split(/\s+/).filter((w) => w.length > 0);
          const readSentences = readText.split(/[.!?]+/).filter((s) => s.trim().length > 0);
          const syllables = readWords.reduce((acc, word) => acc + countSyllables(word), 0);
          const avgWordsPerSentence = readWords.length / (readSentences.length || 1);
          const avgSyllablesPerWord = syllables / (readWords.length || 1);
          const fleschScore = Math.round(
            206.835 - 1.015 * avgWordsPerSentence - 84.6 * avgSyllablesPerWord
          );
          const gradeLevel = Math.round(
            0.39 * avgWordsPerSentence + 11.8 * avgSyllablesPerWord - 15.59
          );
          setResult({
            type: "readability",
            data: {
              fleschScore: Math.max(0, Math.min(100, fleschScore)),
              gradeLevel: Math.max(1, Math.min(16, gradeLevel)),
              wordCount: readWords.length,
              sentenceCount: readSentences.length,
              avgWordsPerSentence: avgWordsPerSentence.toFixed(1),
              difficulty: fleschScore >= 60 ? "Easy" : fleschScore >= 30 ? "Moderate" : "Difficult",
            },
          });
          break;
        }
        default:
          toast.error("Tool not configured");
      }
      toast.success("Done!");
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const usesTextarea = !["seo-audit", "video-downloader", "backlink-checker", "domain-authority-checker"].includes(toolId);

  return (
    <PremiumToolShell
      icon={config.icon}
      eyebrow={toolId === "seo-audit" ? "FREE AUDIT" : "FREE TOOL"}
      title={config.h1}
      description={config.description}
      socialProof={SOCIAL_PROOF}
      footerCTA={<PremiumUpgradeBanner />}
      sideContent={
        <>
          <PremiumFeatureRow
            items={[
              { icon: <Zap className="w-5 h-5" />, title: "Instant results", desc: "Output in seconds" },
              { icon: <Globe className="w-5 h-5" />, title: "100% free", desc: "No credit card" },
              { icon: <CheckCircle2 className="w-5 h-5" />, title: "No signup", desc: "Use it right away" },
            ]}
          />
          <RelatedTools category="free" current={toolId} />
          <ToolSEOContent toolId={toolId} />
        </>
      }
    >
      {/* Input area */}
      <div className="space-y-4">
        {usesTextarea ? (
          <Textarea
            placeholder={config.placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="min-h-[120px] text-base rounded-2xl border-slate-200 focus-visible:ring-[#A855F7]"
            data-testid="free-tool-input"
          />
        ) : (
          <Input
            placeholder={config.placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="text-base py-6 rounded-2xl border-slate-200 focus-visible:ring-[#A855F7]"
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            data-testid="free-tool-input"
          />
        )}

        <Button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full py-6 text-base rounded-2xl bg-[#A855F7] hover:bg-[#9333EA] text-white font-medium shadow-sm"
          data-testid="free-tool-submit"
        >
          {loading && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          {loading ? "Processing…" : config.cta}
        </Button>
      </div>

      {/* Results */}
      {result && (
        <div className="mt-8 pt-8 border-t border-slate-100" data-testid="free-tool-result">
          {result.type === "hashtags" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-[#111111]">
                  {result.data.length} hashtags generated
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full border-slate-200"
                  onClick={() => copyToClipboard(result.data.join(" "))}
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy all
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {result.data.map((tag, i) => (
                  <Badge
                    key={i}
                    className="cursor-pointer bg-[#FAF5FF] text-[#7E22CE] hover:bg-[#F3E8FF] border-0 font-normal"
                    onClick={() => copyToClipboard(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {result.type === "audit" && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="text-5xl font-semibold text-[#111111]">
                  {result.data.overall_score}
                  <span className="text-xl text-slate-400">/100</span>
                </div>
                <div>
                  <p className="font-semibold text-[#111111]">SEO Score</p>
                  <p className="text-sm text-slate-500">
                    {result.data.total_checks || 100}+ checks performed
                  </p>
                </div>
              </div>
              <Link to="/tools/site-audit">
                <Button className="w-full rounded-2xl bg-[#111111] hover:bg-[#000] text-white">
                  View full audit report <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {result.type === "titles" && (
            <div className="space-y-2">
              {result.data.map((title, i) => (
                <div
                  key={i}
                  className="p-4 bg-slate-50 rounded-2xl flex justify-between items-center"
                >
                  <span className="text-[#111111]">{title}</span>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(title)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {result.type === "text" && (
            <div className="p-5 bg-slate-50 rounded-2xl">
              <p className="text-[#111111]">{result.data}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 rounded-full border-slate-200"
                onClick={() => copyToClipboard(result.data)}
              >
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
            </div>
          )}

          {result.type === "video" && result.data.success && (
            <div className="space-y-3">
              <p className="font-semibold text-[#111111]">{result.data.title || "Video ready"}</p>
              <Button
                asChild
                className="w-full rounded-2xl bg-[#111111] hover:bg-[#000] text-white"
              >
                <a href={result.data.download_url} target="_blank" rel="noreferrer">
                  <Download className="w-4 h-4 mr-2" /> Download video
                </a>
              </Button>
            </div>
          )}

          {result.type === "instagram-images" && result.data.success && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-[#111111]">@{result.data.username || "instagram"}</p>
                <span className="text-sm text-slate-500">
                  {result.data.images?.length || 0} images found
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {result.data.images?.slice(0, 4).map((img, idx) => (
                  <div key={idx} className="relative group rounded-2xl overflow-hidden">
                    <img
                      src={img.url}
                      alt={`Instagram ${idx + 1}`}
                      className="w-full h-40 object-cover"
                    />
                    <Button
                      asChild
                      size="sm"
                      className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#111111] text-white rounded-full"
                    >
                      <a href={img.url} target="_blank" rel="noreferrer" download>
                        <Download className="w-3 h-3 mr-1" /> Save
                      </a>
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                className="w-full rounded-2xl bg-[#A855F7] hover:bg-[#9333EA] text-white"
                onClick={() => {
                  result.data.images?.forEach((img) => window.open(img.url, "_blank"));
                }}
              >
                <Download className="w-4 h-4 mr-2" /> Download all
              </Button>
            </div>
          )}

          {result.type === "wordcount" && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-[#111111]">Text analysis</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <PremiumStat value={result.data.words} label="Words" accent />
                <PremiumStat value={result.data.characters} label="Characters" />
                <PremiumStat value={result.data.charactersNoSpaces} label="Characters (no spaces)" />
                <PremiumStat value={result.data.sentences} label="Sentences" />
                <PremiumStat value={result.data.paragraphs} label="Paragraphs" />
                <PremiumStat value={result.data.readingTime} label="Min read" />
              </div>
            </div>
          )}

          {result.type === "keywords" && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-[#111111]">Keyword research</h3>
              <div className="grid grid-cols-2 gap-3 mb-2">
                <PremiumStat
                  value={result.data.search_volume?.toLocaleString() || "N/A"}
                  label="Monthly searches"
                  accent
                />
                <PremiumStat value={result.data.difficulty || "Medium"} label="Difficulty" />
              </div>
              {result.data.related_keywords?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 text-[#111111]">Related keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {result.data.related_keywords.slice(0, 15).map((kw, i) => (
                      <Badge
                        key={i}
                        variant="outline"
                        className="cursor-pointer rounded-full border-slate-200 text-slate-700"
                        onClick={() => copyToClipboard(kw)}
                      >
                        {kw}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <Link to="/tools/seo?tool=keyword-research">
                <Button className="w-full mt-4 rounded-2xl bg-[#111111] hover:bg-[#000] text-white">
                  View full keyword report <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {result.type === "backlinks" && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-[#111111]">Backlink analysis</h3>
              <div className="grid grid-cols-3 gap-3">
                <PremiumStat
                  value={result.data.total_backlinks?.toLocaleString() || "0"}
                  label="Total backlinks"
                  accent
                />
                <PremiumStat
                  value={result.data.referring_domains || "0"}
                  label="Referring domains"
                />
                <PremiumStat
                  value={result.data.domain_authority || "N/A"}
                  label="Domain authority"
                />
              </div>
              <Link to="/tools/seo?tool=backlink-analyzer">
                <Button className="w-full rounded-2xl bg-[#111111] hover:bg-[#000] text-white">
                  View full backlink report <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {result.type === "plagiarism" && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-[#111111]">Plagiarism check</h3>
              <div className="flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-[#FAF5FF] border border-[#A855F7]/20 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-4xl font-semibold text-[#A855F7]">
                      {result.data.score}%
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{result.data.status}</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <PremiumStat value={result.data.wordCount} label="Words checked" />
                <PremiumStat value={result.data.uniqueWords} label="Unique words" />
              </div>
            </div>
          )}

          {result.type === "domain-authority" && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-[#111111]">Domain authority</h3>
              <div className="flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-[#111111] flex items-center justify-center text-white">
                  <div className="text-center">
                    <p className="text-4xl font-semibold">
                      {result.data.da_score || result.data.authority_score || "N/A"}
                    </p>
                    <p className="text-xs opacity-70">DA Score</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <PremiumStat value={result.data.page_authority || "N/A"} label="Page authority" />
                <PremiumStat value={result.data.spam_score || "Low"} label="Spam score" />
              </div>
            </div>
          )}

          {result.type === "serp" && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-[#111111]">
                SERP results for &ldquo;{input}&rdquo;
              </h3>
              <div className="space-y-2">
                {result.data.results?.slice(0, 5).map((item, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 bg-[#111111] text-white rounded-full flex items-center justify-center text-sm font-semibold">
                        {i + 1}
                      </span>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#111111] hover:text-[#A855F7] truncate flex-1"
                      >
                        {item.title || item.url}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/tools/seo?tool=rank-tracker">
                <Button className="w-full rounded-2xl bg-[#111111] hover:bg-[#000] text-white">
                  Track this keyword <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {result.type === "readability" && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-[#111111]">Readability</h3>
              <div className="flex items-center justify-center mb-4">
                <div className="w-32 h-32 rounded-full bg-[#FAF5FF] border border-[#A855F7]/20 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-4xl font-semibold text-[#A855F7]">
                      {result.data.fleschScore}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{result.data.difficulty}</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <PremiumStat value={result.data.gradeLevel} label="Grade level" />
                <PremiumStat value={result.data.wordCount} label="Words" />
                <PremiumStat value={result.data.sentenceCount} label="Sentences" />
                <PremiumStat
                  value={result.data.avgWordsPerSentence}
                  label="Avg words / sentence"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </PremiumToolShell>
  );
}
